import React, { Suspense, useEffect } from 'react';
import dayjs from 'dayjs';

import { useCustomer } from './Common/hooks/useCustomer';
import { useIsMobile } from './Common/hooks/useIsMobile';
import { useLiveChat } from './Common/hooks/useLiveChat';
import { useBrand, useBrandName } from './Common/hooks/useBrand';
import { useServices } from './Services/hooks/useServices';
import { isDoctorPortal } from './Common/utils/isDoctorPortal';
import DocumentTitle from 'react-document-title';
import { Box, StatusBar } from 'native-base';
import { Navigate, Outlet, Route, Routes, useLocation } from './Common/components/Navigation';
import { useRecoilValue } from 'recoil';
import { authenticationStateAtom } from './Common/state/authenticationState';
import Loading from './Common/components/Loading';
import { Platform } from 'react-native';
import PendingDispenseQLD from "./Fufillment/views/PendingDispenseQLD";
import PendingDispenseNonQLD from "./Fufillment/views/PendingDispenseNonQLD";
import {PendingPickupStrathpine} from "./Fufillment/components/PendingPickupStrathpine";
import {PendingPickupNewtown} from "./Fufillment/components/PendingPIckupNewtown";
import { PendingSameDayStrathpine } from './Fufillment/components/PendingSameDayStrathpine';
import { PendingSameDayNewtown } from './Fufillment/components/PendingSameDayNewtown';
import PendingBooking from './Fufillment/views/PendingBooking';

const PortalHeader = React.lazy(() => import('./Header/PortalHeader'));
const PendingDispense = React.lazy(() => import('./Fufillment/views/PendingDispense'));
const RequestWrapper = React.lazy(() => import('./Request/components/RequestWrapper'));
const Feedback = React.lazy(() => import('./Feedback/components/Feedback'));

const relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

const MedicalInformation = React.lazy(
  () => import('./MedicalInformation/components/MedicalInformation'),
);
const DoctorRequest = React.lazy(() => import('./Request/components/DoctorRequest'));
const DoctorList = React.lazy(() => import('./DoctorList/components/DoctorList'));
const Request = React.lazy(() => import('./Request/components/Request'));
const Payment = React.lazy(() => import('./Payment/components/Payment'));
const Start = React.lazy(() => import('./Start/Start'));
const Info = React.lazy(() => import('./Info/components/Info'));
const Login = React.lazy(() => import('./Login/components/Login'));
const DoctorLogin = React.lazy(() => import('./Login/components/DoctorLogin'));
const Services = React.lazy(() => import('./Services/components/Services'));
const PrivacyPolicy = React.lazy(() => import('./Legal/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./Legal/TermsOfUse'));
const AppointmentBookingFlow = React.lazy(
  () => import('./AppointmentBooking/components/FlowBooking'),
);
const NotFound = React.lazy(() => import('./NotFound/components/NotFound'));
const PageForm = React.lazy(() => import('./Form/components/PageForm'));
const PageBooking = React.lazy(() => import('./AppointmentBooking/components/PageBooking'));

const LeafDoctorsSignup = React.lazy(() => import('./LeafDoctors/Signup/Signup'));

const ProfileTrueGreen = React.lazy(() => import('./Profile/components/LeafDoctors/Profile'));
const UpdateProfile = React.lazy(() => import('./Profile/components/ProfileDetails'));
const DoctorAppointments = React.lazy(
  () => import('./DoctorAppointments/components/DoctorAppointments'),
);
const ProfileTrueGreenTreatmentPlan = React.lazy(
  () => import('./Profile/components/LeafDoctors/Home'),
);
const ProfileTrueGreenMessaging = React.lazy(
  () => import('./Profile/components/LeafDoctors/Messaging'),
);
const ProfileTrueGreenOrders = React.lazy(() => import('./Profile/components/LeafDoctors/Orders'));
const ProfileTrueGreenOrderRedirect = React.lazy(
  () => import('./Profile/components/LeafDoctors/OrderRedirect'),
);

const ProfileTrueGreenOrder = React.lazy(() => import('./Profile/components/LeafDoctors/Order'));
const ProfileTrueGreenSubscription = React.lazy(
  () => import('./Profile/components/LeafDoctors/Subscription'),
);
const ProfileTrueGreenAppointments = React.lazy(
  () => import('./Profile/components/LeafDoctors/Appointments'),
);
const CustomerList = React.lazy(() => import('./CustomerList/components/CustomerList'));
const DoctorHome = React.lazy(() => import('./DoctorHome/components/DoctorHome'));
const DoctorCustomer = React.lazy(() => import('./DoctorCustomer/components/DoctorCustomer'));

const DoctorUnreviewedResults = React.lazy(() => import('./DoctorPathology/views/Results'));
const DoctorPathologyResult = React.lazy(() => import('./DoctorPathology/views/Result'));
const DoctorScheduling = React.lazy(() => import('./DoctorScheduling/views/DoctorScheduling'));
const DoctorPathologyRequests = React.lazy(() => import('./DoctorPathology/views/Requests'));
const DoctorPathologyRequest = React.lazy(() => import('./DoctorPathology/views/Request'));

const Fufillment = React.lazy(() => import('./Fufillment/views/Fufillment'));
const FufillmentCompleted = React.lazy(() => import('./Fufillment/views/Completed'));
const FufillmentNewOrders = React.lazy(() => import('./Fufillment/views/NewOrders'));

const MedicationsManage = React.lazy(() => import('./DoctorMedications/views/MedicationsManage'));

const DoctorTasks = React.lazy(() => import('./DoctorTasks/components/Tasks'));
const Subscriptions = React.lazy(() => import('./Subscriptions/components/Subscriptions'));

const DoctorMessagesInbox = React.lazy(() => import('./Messages/views/Inbox'));
const DoctorMessagesInboxMessages = React.lazy(() => import('./Messages/views/InboxMessages'));

const Reports = React.lazy(() => import('./Reports/components/Reports'));
const ReportsReport = React.lazy(() => import('./Reports/components/Report'));

function AuthProtectedRoute({ children }: any) {
  const { signedin, loading } = useCustomer();

  const { pathname } = useLocation();

  if (loading) return <Loading />;

  if (!signedin) {
    return <Navigate to={`/signin?redirect=${pathname}`} replace />;
  }

  return children || <Outlet />;
}

const doctorPortal = isDoctorPortal();

function WebApp() {
  const { isAuthenticated } = useRecoilValue(authenticationStateAtom);
  const { isMobile } = useIsMobile();

  useLiveChat();
  useServices();

  const { name, backgroundColor } = useBrandName();

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.body.style.background = backgroundColor;
    }
  }, []);

  const brand = useBrand();

  return (
    <Box backgroundColor={backgroundColor} height={'100%'}>
      <StatusBar backgroundColor={backgroundColor} barStyle="dark-content" />

      {doctorPortal && <PortalHeader />}

      <Suspense fallback={<Loading />}>

        <DocumentTitle title={name} />

        <Routes>

          {!doctorPortal && brand === 'leafdoctors' && (
            <>
              <Route element={<LeafDoctorsSignup />} path={'/signup'} />

              <Route
                element={
                  <AuthProtectedRoute>
                    <ProfileTrueGreen />
                  </AuthProtectedRoute>
                }
                path="/profile"
              >
                <Route element={<ProfileTrueGreenTreatmentPlan />} path={''} />
                <Route element={<ProfileTrueGreenMessaging />} path={'messages'} />
                <Route element={<ProfileTrueGreenOrderRedirect />} path={'order-redirect'} />
                <Route element={<ProfileTrueGreenOrder />} path={'order'} />
                <Route element={<ProfileTrueGreenOrders />} path={'orders'} />
                <Route element={<ProfileTrueGreenAppointments />} path={'appointments'} />
                <Route element={<ProfileTrueGreenSubscription />} path={'auto-refills'} />
              </Route>
            </>
          )}

          <Route element={isDoctorPortal() ? <DoctorLogin /> : <Login />} path="/signin" />

          {!doctorPortal && (
            <>
              <Route element={<Services />} path="/services" />

              <Route element={<TermsOfUse />} path="/terms-of-use" />

              <Route element={<Feedback />} path="/feedback" />

              <Route element={<PrivacyPolicy />} path="/privacy-policy" />

              <Route element={<Start />} path="/start" />

              <Route element={<Info />} path="/start/info" />

              <Route element={<Info />} path="/start/info" />

              <Route element={<AppointmentBookingFlow />} path="/start/schedule" />

              <Route element={<MedicalInformation />} path="/start/medical" />

              <Route element={<Payment />} path="/start/payment" />

              <Route
                element={
                  <AuthProtectedRoute>
                    <RequestWrapper>
                      <Request />
                    </RequestWrapper>
                  </AuthProtectedRoute>
                }
                path="/request/:requestID"
              />

              <Route
                element={
                  <AuthProtectedRoute>
                    <PageBooking />
                  </AuthProtectedRoute>
                }
                path="/book/:requestID"
              />

              <Route
                element={
                  <AuthProtectedRoute>
                    <PageForm />
                  </AuthProtectedRoute>
                }
                path="/form/:formID/:requestID"
              />

              <Route
                element={
                  <AuthProtectedRoute>
                    <UpdateProfile />
                  </AuthProtectedRoute>
                }
                path="/profile/update"
              />
            </>
          )}

          {!isAuthenticated && doctorPortal && (
            <>
              <Route path="*" element={<Navigate replace to="/signin?redirect=/" />} />
            </>
          )}

          {isAuthenticated && doctorPortal && (
            <>
              <Route element={<DoctorHome />} path="/" />

              <Route element={<DoctorScheduling />} path="/scheduling" />

              <Route element={<DoctorMessagesInbox />} path="/inbox">
                <Route element={<DoctorMessagesInboxMessages />} path="/inbox/:ID" />
              </Route>

              <Route element={<Reports />} path="/reports">
                <Route element={<ReportsReport />} path="/reports/:ID" />
              </Route>

              <Route element={<DoctorAppointments />} path="/appointments/:type/:date" />

              <Route element={<Fufillment />} path="/fulfill">
                <Route element={<FufillmentNewOrders />} path="/fulfill/new-orders" />
                <Route element={<PendingBooking />} path="/fulfill/pending-booking" />
                <Route element={<PendingDispense />} path="/fulfill/pending-dispense" />
                <Route element={<PendingDispenseQLD />} path="/fulfill/pending-dispense-qld" />
                <Route element={<PendingDispenseNonQLD />} path="/fulfill/pending-dispense-non-qld" />
                <Route element={<PendingPickupStrathpine />} path="/fulfill/pending-pickup-qld" />
                <Route element={<PendingPickupNewtown />} path="/fulfill/pending-pickup-non-qld" />
                <Route element={<PendingSameDayStrathpine />} path="/fulfill/pending-same-day-qld" />
                <Route element={<PendingSameDayNewtown />} path="/fulfill/pending-same-day-non-qld" />

                <Route element={<FufillmentCompleted />} path="/fulfill/completed" />
              </Route>

              <Route element={<MedicationsManage />} path="/medications" />

              <Route element={<DoctorTasks />} path="/tasks" />

              <Route element={<Subscriptions />} path="/auto-refills" />

              <Route element={<CustomerList />} path="/customers" />

              <Route element={<DoctorCustomer />} path="/customers/:customerID" />

              {!isMobile && (
                <Route element={<DoctorUnreviewedResults />} path="/pathology/results">
                  <Route element={<DoctorPathologyResult />} path="/pathology/results/:resultID" />
                </Route>
              )}

              {!isMobile && (
                <Route element={<DoctorPathologyRequests />} path="/pathology/requests">
                  <Route
                    element={<DoctorPathologyRequest />}
                    path="/pathology/requests/:requestID"
                  />
                </Route>
              )}

              {isMobile && (
                <>
                  <Route element={<DoctorUnreviewedResults />} path="/pathology/results" />
                  <Route element={<DoctorPathologyResult />} path="/pathology/results/:resultID" />
                </>
              )}

              <Route element={<DoctorRequest />} path="/request/:brand/:requestID" />

              {!isMobile && (
                <Route element={<DoctorList />} path="/requests/:brand">
                  <Route element={<DoctorRequest />} path="/requests/:brand/:requestID" />
                </Route>
              )}

              {isMobile && (
                <>
                  <Route element={<DoctorList />} path="/requests/:brand" />

                  <Route element={<DoctorRequest />} path="/requests/:brand/:requestID" />
                </>
              )}
            </>
          )}

          <Route path="/404" element={<NotFound />} />

          {brand === 'leafdoctors' ? (
            brand === 'leafdoctors' && isMobile ? (
              <Route path="*" element={<Navigate replace to="/profile" />} />
            ) : (
              <Route path="*" element={<Navigate replace to="/services" />} />
            )
          ) : (
            <Route path="*" element={<Navigate replace to="/404" />} />
          )}
        </Routes>
      </Suspense>
    </Box>
  );
}

export default WebApp;
